import type { Core as ApryseCore, WebViewerInstance } from '@pdftron/webviewer';
import { Dispatch, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { SUGGESTION_SUBJECT } from 'modules/common/viewer/pdfViewer/annotationCommands';
import {
  updatePageScale,
  viewerAnnotations,
  viewerMeasurements,
} from 'modules/common/viewer/pdfViewer/data/actions';
import {
  ANNOTATION_CHANGE_EVENT,
  REDACT_SUBJECT,
} from 'modules/common/viewer/pdfViewer/data/constants';

import { formatFloat } from 'utils/formatters';

const getXmlDoc = async (
  annotationManager: ApryseCore.AnnotationManager,
): Promise<Document> => {
  const xfdfCommandString = await annotationManager.exportAnnotationCommand();
  const xmlParser = new DOMParser();
  return xmlParser.parseFromString(xfdfCommandString, 'text/xml');
};

// Measurement annotations are a loose collection of annotations that have a Scale property.
type MeasurementAnnotation =
  | ApryseCore.Annotations.ArcAnnotation
  | ApryseCore.Annotations.EllipseAnnotation
  | ApryseCore.Annotations.LineAnnotation
  | ApryseCore.Annotations.PolylineAnnotation
  | ApryseCore.Annotations.PolygonAnnotation;

// NB: 'Measure' actually doesn't show up anywhere as a property in the type definitions, but in Apryse's examples
// such as: https://docs.apryse.com/web/guides/measurement-annotations, the property does exist.
export const isMeasurementAnnotation = (
  annotation: ApryseCore.Annotations.Annotation,
): annotation is MeasurementAnnotation => {
  return 'Measure' in annotation && !!annotation.Measure;
};

const addAnnotations = (
  instance: WebViewerInstance,
  annotations: ApryseCore.Annotations.Annotation[],
  xmlDoc: Document,
  dispatch: Dispatch<unknown>,
) => {
  const data = Array.from(
    xmlDoc.getElementsByTagName('add')[0]?.children || [],
  );
  annotations.forEach((annotation) => {
    if (annotation.Subject === SUGGESTION_SUBJECT) return;
    if (annotation.Subject === REDACT_SUBJECT) return;

    const annotationXml = data.find(
      (annot) => annot.getAttribute('name') === annotation.Id,
    );
    if (annotationXml) {
      const xfdfString = new XMLSerializer().serializeToString(annotationXml);

      if (isMeasurementAnnotation(annotation)) {
        // update page scale request based on scale of measurement
        dispatch(
          updatePageScale.request({
            annotationChangeEvent: ANNOTATION_CHANGE_EVENT.ADD,
            pageNumber: annotation.getPageNumber(),
            scale: {
              scaleFromDimension: formatFloat(annotation.Scale[0][0], 16, true),
              scaleFromUnit: annotation.Scale[0][1],
              scaleToDimension: formatFloat(annotation.Scale[1][0], 16, true),
              scaleToUnit: annotation.Scale[1][1],
            },
          }),
        );

        // actually add the viewer measurement
        dispatch(
          viewerMeasurements.add({
            id: annotation.Id,
            zoom: instance.UI.getZoomLevel(),
            xfdfString,
          }),
        );
      } else {
        // add the viewer annotation
        dispatch(
          viewerAnnotations.add({
            id: annotation.Id,
            zoom: instance.UI.getZoomLevel(),
            xfdfString,
          }),
        );
      }
    }
  });
};

const modifyAnnotations = (
  annotations: ApryseCore.Annotations.Annotation[],
  xmlDoc: Document,
  dispatch: Dispatch<unknown>,
) => {
  const data = Array.from(
    xmlDoc.getElementsByTagName('modify')[0]?.children || [],
  );
  annotations.forEach((annotation) => {
    if (annotation.Subject === SUGGESTION_SUBJECT) return;
    const annotationXml = data.find(
      (annot) => annot.getAttribute('name') === annotation.Id,
    );
    if (annotationXml) {
      const xfdfString = new XMLSerializer().serializeToString(annotationXml);
      if (isMeasurementAnnotation(annotation)) {
        // update page scales
        dispatch(
          updatePageScale.request({
            annotationChangeEvent: ANNOTATION_CHANGE_EVENT.MODIFY,
            pageNumber: annotation.getPageNumber(),
            scale: {
              scaleFromDimension: formatFloat(annotation.Scale[0][0], 16, true),
              scaleFromUnit: annotation.Scale[0][1],
              scaleToDimension: formatFloat(annotation.Scale[1][0], 16, true),
              scaleToUnit: annotation.Scale[1][1],
            },
          }),
        );
        // modify viewer measurement
        dispatch(
          viewerMeasurements.modify({
            id: annotation.Id,
            xfdfString,
          }),
        );
      } else {
        // must be a standard annotation if does not have measure property
        dispatch(
          viewerAnnotations.modify({
            id: annotation.Id,
            xfdfString,
          }),
        );
      }
    }
  });
};

const deleteAnnotations = (
  annotations: ApryseCore.Annotations.Annotation[],
  dispatch: Dispatch<unknown>,
) => {
  const ids = annotations
    .filter((annotation) => annotation.Subject !== SUGGESTION_SUBJECT)
    .map((annotation) => annotation.Id);
  dispatch(viewerAnnotations.delete({ ids }));
  dispatch(viewerMeasurements.delete({ ids }));
};

type UseAnnotationEventListenersProps = {
  instance: WebViewerInstance | null;
  selectedAnnotationIds: Set<string>;
  setSelectedAnnotationIds: (nextSet: Set<string>) => void;
};

export const useAnnotationEventListeners = ({
  instance,
  selectedAnnotationIds,
  setSelectedAnnotationIds,
}: UseAnnotationEventListenersProps) => {
  const dispatch = useDispatch();

  // handle setting up annotationChanged changed callbacks
  useEffect(() => {
    if (!instance) return;

    instance.Core.annotationManager.addEventListener(
      'annotationChanged',
      async (annotations, action, { imported }) => {
        if (imported) {
          return;
        }

        const xmlDoc = await getXmlDoc(instance.Core.annotationManager);

        switch (action) {
          case 'add': {
            addAnnotations(instance, annotations, xmlDoc, dispatch);
            break;
          }
          case 'modify': {
            modifyAnnotations(annotations, xmlDoc, dispatch);
            break;
          }
          case 'delete': {
            deleteAnnotations(annotations, dispatch);
            break;
          }
        }
      },
    );
  }, [dispatch, instance]);

  // handle on select callback updating. Dependent on outer state so we do not
  // want to redundantly set callbacks
  useEffect(() => {
    if (!instance) return;

    instance.Core.annotationManager.addEventListener(
      'annotationSelected.customSelectionBehavior',
      () => {
        const newSelections = new Set(
          instance.Core.annotationManager
            .getSelectedAnnotations()
            .filter((annot) => annot.Subject !== SUGGESTION_SUBJECT)
            .map(({ Id }) => Id),
        );
        if (
          newSelections.size !== selectedAnnotationIds.size ||
          newSelections.difference(selectedAnnotationIds).size > 0
        ) {
          setSelectedAnnotationIds(newSelections);
        }
      },
    );

    return () => {
      instance.Core.annotationManager.removeEventListener(
        'annotationSelected.customSelectionBehavior',
      );
    };
  }, [instance, selectedAnnotationIds, setSelectedAnnotationIds]);
};
